import { Allocation } from "@/api/SolarCloudApi";
import LoadingSection from "@/components/LoadingSection";
import H1 from "@/components/ui/H1";
import { useUsersOrders } from "@/queries/order/useUsersOrders";
import { usePowerGenerationInfo } from "@/queries/user/usePowerGenerationInfo";
import { useUser } from "@/queries/user/useUser";
import Allocations from "./Allocations";
import AddAllocationDialog from "./AddAllocationDialog";
import { cn } from "@/util/cn";
import { useEffect, useMemo, useState } from "react";
import { useAllocations } from "./useAllocations";
import PanelCard from "./PanelCard";
import GasInformationAccordion from "./GasInformationAccordion";

// Types
interface AllocationSummaryProps {
  totalCapacity: number;
  totalAllocated: number;
  isFullyAllocated: boolean;
}

// Components
const AllocationSummary = ({
  totalCapacity,
  totalAllocated,
  isFullyAllocated,
}: AllocationSummaryProps) => {
  const totalPanels = totalCapacity / 100;

  return (
    <div className="w-full space-y-4">
      {totalCapacity >= 0 && (
        <p className={cn("text-center", isFullyAllocated && "mb-4")}>
          You own {totalPanels} panels. That is {totalCapacity / 1000} kW.
        </p>
      )}
      {totalCapacity === 0 && (
        <p className="pb-4 text-center">
          Order panels today to start generating power.
        </p>
      )}
      {!isFullyAllocated && (
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 mx-auto w-full">
          <div>
            <h4 className="text-center">Linked</h4>
            <PanelCard
              panels={totalAllocated / 100}
              capacity={totalAllocated}
              className="bg-brand-neutral-900"
            />
          </div>
          <div>
            <h4 className="text-center">Unlinked</h4>
            <PanelCard
              panels={totalPanels - totalAllocated / 100}
              capacity={totalCapacity - totalAllocated}
              className="bg-brand-neutral-900/95"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default function AllocationsPanel({
  account,
}: {
  account?: "gas" | "electricity" | "bank";
}) {
  const { data: user, isLoading: isUserLoading } = useUser();
  const { data: powerGenerationInfo, isLoading: isPowerGenLoading } =
    usePowerGenerationInfo({
      queryConfig: {
        enabled: !!user,
      },
    });
  const { data: usersOrders, isLoading: isOrdersLoading } = useUsersOrders({
    queryConfig: {
      enabled: !!user,
    },
  });

  const {
    allocations,
    totalAllocated,
    addAllocation,
    deleteAllocation,
    updateAllocation,
    moveAllocation,
    isLoading,
    error,
  } = useAllocations({ userId: user?.uuid, account });

  const filteredAllocations = useMemo(() => {
    if (!allocations) return [];

    return allocations
      .filter((allocation) => {
        if (account === "gas") {
          return (
            allocation.nickname?.startsWith("::gas::") &&
            allocation.energyCompanyPayee !== null
          );
        }
        if (account === "electricity") {
          return (
            !allocation.bankPayee &&
            !allocation.nickname?.startsWith("::gas::") &&
            allocation.energyCompanyPayee !== null
          );
        }
        if (account === "bank") {
          return allocation.bankPayee !== null && allocation.bankPayee !== undefined;
        }
        return true;
      })
      .map((allocation) => {
        // remove any leading ::something:: prefix
        const cleanNickname = allocation.nickname?.replace(/^::\w+::/, "");
        return {
          ...allocation,
          nickname: cleanNickname,
        };
      });
  }, [allocations, account]);

  const [accordionOpen, setAccordionOpen] = useState<boolean>(false);

  const handleAddAllocation = (allocation: Allocation) => {
    addAllocation(allocation);
    setAccordionOpen(false);
  };

  const totalCapacity = useMemo(
    () => powerGenerationInfo?.user?.active ?? 0,
    [powerGenerationInfo?.user?.active]
  );
  const isFullyAllocated = useMemo(
    () => totalAllocated >= totalCapacity,
    [totalAllocated, totalCapacity]
  );

  useEffect(() => {
    if (allocations && filteredAllocations.length === 0) {
      setAccordionOpen(true);
    }
  }, [setAccordionOpen, allocations, filteredAllocations]);

  if (isLoading || isUserLoading || isPowerGenLoading || isOrdersLoading) {
    return <LoadingSection />;
  }

  if (error) {
    return (
      <div className="text-red-500">
        Error loading allocations: {error.message}
      </div>
    );
  }

  const getTitle = () => {
    if (account === "electricity") {
      return "Electricity Account";
    } else if (account === "gas") {
      return "Gas Account";
    }
    return "Allocations";
  };

  return (
    <>
      <div className="mx-auto max-w-screen-md pt-12 pb-4">
        <div className="mx-auto w-4/5">
          <h2>Link My:</h2>
        </div>

        <H1 className="text-center pb-8">{getTitle()}</H1>

        <div className="relative">
          <AllocationSummary
            totalCapacity={totalCapacity}
            totalAllocated={totalAllocated}
            isFullyAllocated={isFullyAllocated}
          />
          {!isFullyAllocated && (
            <>
              {account === "electricity" &&
                filteredAllocations.length === 0 && (
                  <p className="mx-auto max-w-lg pt-4 text-center">
                    Please have your energy bill ready as we require some
                    information so we credit the correct electricity bill.
                  </p>
                )}
              {account === "gas" && (
                <GasInformationAccordion
                  defaultOpen={filteredAllocations.length === 0}
                  open={accordionOpen}
                  onOpenChange={setAccordionOpen}
                />
              )}

              <AddAllocationDialog
                handleAddAllocation={handleAddAllocation}
                unallocatedWatts={totalCapacity - totalAllocated}
                className="absolute -bottom-10 right-0 sm:right-10 lg:right-20"
                account={account}
              />
            </>
          )}
        </div>
      </div>
      <div className="mx-auto max-w-screen-md">
        <Allocations
          allocations={filteredAllocations}
          deleteAllocation={deleteAllocation}
          updateAllocation={updateAllocation}
          moveAllocation={moveAllocation}
          unallocatedWatts={totalCapacity - totalAllocated}
          account={account}
        />
      </div>
    </>
  );
}
